import React, { useState } from 'react';
import {
  Card,
  Space,
  Tag,
  Row,
  Col,
  Typography,
  Descriptions,
  Table,
  Empty,
  Spin,
  Divider
} from 'antd';
import {
  InsuranceOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  DownOutlined,
  UpOutlined
} from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import { useTranslation } from '../../hooks/useTranslation';
import { GET_INSURANCE_CLAIMS_BY_APPOINTMENT } from '../../graphql/queries';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

interface InsuranceClaimCardProps {
  appointmentId: string;
  insuranceClaims?: any[]; // Optional: if provided, use these instead of fetching
  insuranceClaimsLoading?: boolean; // Optional: loading state if insuranceClaims are provided
  onRefetch?: () => void; // Optional: refetch callback if insuranceClaims are provided
  style?: React.CSSProperties;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
}

// Helper function to get status color
const getStatusColor = (status: string): string => {
  switch (status) {
    case 'approved':
      return 'green';
    case 'partially_approved':
      return 'orange';
    case 'rejected':
      return 'red';
    case 'pending':
      return 'blue';
    default:
      return 'default';
  }
};

// Helper function to get status icon
const getStatusIcon = (status: string) => {
  switch (status) {
    case 'approved':
      return <CheckCircleOutlined />;
    case 'partially_approved':
      return <ExclamationCircleOutlined />;
    case 'rejected':
      return <CloseCircleOutlined />;
    case 'pending':
      return <ClockCircleOutlined />;
    default:
      return <ClockCircleOutlined />;
  }
};

// Helper function to get status display text
const getStatusDisplay = (status: string, t: (k: string) => string): string => {
  switch (status) {
    case 'approved':
      return t('insurance.claims.status.approved') || 'Approved';
    case 'partially_approved':
      return t('insurance.claims.status.partiallyApproved') || 'Partially Approved';
    case 'rejected':
      return t('insurance.claims.status.rejected') || 'Rejected';
    case 'pending':
      return t('insurance.claims.status.pending') || 'Pending';
    default:
      return status;
  }
};

const InsuranceClaimCard: React.FC<InsuranceClaimCardProps> = ({
  appointmentId,
  insuranceClaims: providedInsuranceClaims,
  insuranceClaimsLoading: providedLoading,
  onRefetch,
  style,
  collapsible = false,
  defaultCollapsed = false
}) => {
  const { t } = useTranslation();
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

  // Fetch insurance claims only if not provided as prop
  const { data, loading, error } = useQuery(GET_INSURANCE_CLAIMS_BY_APPOINTMENT, {
    variables: { appointmentId },
    skip: !appointmentId || providedInsuranceClaims !== undefined,
    fetchPolicy: 'cache-and-network',
  });

  // Use provided insurance claims or fetch from query
  const insuranceClaims = providedInsuranceClaims !== undefined ? providedInsuranceClaims : (data?.insuranceClaimResultsByAppointment || []);
  const isLoading = providedLoading !== undefined ? providedLoading : loading;

  const toggleCollapse = () => {
    if (collapsible) {
      setIsCollapsed(!isCollapsed);
    }
  };

  // Collect all items from all claims with claim information
  const allItems = insuranceClaims.flatMap((claim: any) => {
    if (!claim.items || claim.items.length === 0) return [];
    return claim.items.map((item: any) => ({
      ...item,
      claimId: claim.id,
      claimStatus: claim.status,
      policyId: claim.policyId,
    }));
  });

  // Table columns for all claim items
  const itemColumns = [
    {
      title: t('insurance.claims.claimId') || 'Claim ID',
      dataIndex: 'claimId',
      key: 'claimId',
      render: (claimId: string) => <Text code>{claimId.slice(-6)}</Text>,
      width: 120,
    },
    {
      title: t('insurance.claims.status.title') || 'Status',
      dataIndex: 'claimStatus',
      key: 'claimStatus',
      render: (status: string) => (
        <Tag color={getStatusColor(status)} icon={getStatusIcon(status)}>
          {getStatusDisplay(status, t)}
        </Tag>
      ),
      width: 150,
    },
    {
      title: t('insurance.claims.items.name') || 'Item Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('insurance.claims.items.category') || 'Category',
      dataIndex: 'category',
      key: 'category',
    },
    {
      title: t('insurance.claims.items.amount') || 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: string) => `$${parseFloat(amount || '0').toFixed(2)}`,
      align: 'right' as const,
      width: 120,
    },
    {
      title: t('insurance.claims.items.approvedAmount') || 'Approved Amount',
      dataIndex: 'approvedAmount',
      key: 'approvedAmount',
      render: (approvedAmount: string) => (
        <Text strong style={{ color: '#52c41a' }}>
          ${parseFloat(approvedAmount || '0').toFixed(2)}
        </Text>
      ),
      align: 'right' as const,
      width: 150,
    },
  ];

  // Calculate totals
  const totalAmount = allItems.reduce(
    (sum: number, item: any) => sum + parseFloat(item.amount || '0'),
    0
  );
  const totalApprovedAmount = allItems.reduce(
    (sum: number, item: any) => sum + parseFloat(item.approvedAmount || '0'),
    0
  );

  const renderCollapsedView = () => {
    if (insuranceClaims.length === 0) {
      return (
        <Text type="secondary">
          {t('insurance.claims.noClaims') || 'No insurance claims'}
        </Text>
      );
    }
    return (
      <Space>
        <InsuranceOutlined />
        <Text>
          {insuranceClaims.length} {t('insurance.claims.claim') || 'claim(s)'}
        </Text>
        <Tag color={getStatusColor(insuranceClaims[0]?.status)}>
          {getStatusDisplay(insuranceClaims[0]?.status, t)}
        </Tag>
      </Space>
    );
  };

  if (isLoading) {
    return (
      <Card
        title={t('insurance.claims.title') || 'Insurance Claims'}
        style={{ marginBottom: 24, ...style }}
      >
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin />
        </div>
      </Card>
    );
  }

  if (error && providedInsuranceClaims === undefined) {
    return (
      <Card
        title={t('insurance.claims.title') || 'Insurance Claims'}
        style={{ marginBottom: 24, ...style }}
      >
        <Text type="danger">
          {t('insurance.claims.error') || 'Error loading insurance claims'}
        </Text>
      </Card>
    );
  }

  return (
    <Card
      title={
        <div
          style={{
            cursor: collapsible ? 'pointer' : 'default',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
          onClick={toggleCollapse}
        >
          <Space>
            <InsuranceOutlined />
            <span>{t('insurance.claims.title') || 'Insurance Claims'}</span>
            {insuranceClaims.length > 0 && (
              <Tag>{insuranceClaims.length}</Tag>
            )}
          </Space>
          {collapsible && (
            <span style={{ fontSize: '12px', color: '#8c8c8c' }}>
              {isCollapsed ? <DownOutlined /> : <UpOutlined />}
            </span>
          )}
        </div>
      }
      style={{ marginBottom: 24, ...style }}
    >
      {!isCollapsed && (
        <>
          {insuranceClaims.length === 0 ? (
            <Empty
              description={t('insurance.claims.noClaims') || 'No insurance claims found for this appointment'}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : (
            <div>
              {/* Summary of all claims */}
              <Descriptions column={3} size="small" bordered style={{ marginBottom: 16 }}>
                <Descriptions.Item label={t('insurance.claims.totalAmount') || 'Total Amount'}>
                  <Text strong>${totalAmount.toFixed(2)}</Text>
                </Descriptions.Item>
                <Descriptions.Item label={t('insurance.claims.totalApprovedAmount') || 'Total Approved'}>
                  <Text strong style={{ color: '#52c41a' }}>
                    ${totalApprovedAmount.toFixed(2)}
                  </Text>
                </Descriptions.Item>
                <Descriptions.Item label={t('insurance.claims.claim') || 'Claims'}>
                  <Text>{insuranceClaims.length}</Text>
                </Descriptions.Item>
              </Descriptions>

              {/* All items in a single table */}
              {allItems.length > 0 ? (
                <Table
                  columns={itemColumns}
                  dataSource={allItems}
                  rowKey="id"
                  pagination={allItems.length > 10 ? { pageSize: 10 } : false}
                  size="small"
                  summary={(pageData) => {
                    if (pageData.length === 0) return null;
                    
                    const pageTotal = pageData.reduce(
                      (sum: number, item: any) => sum + parseFloat(item.amount || '0'),
                      0
                    );
                    const pageApprovedTotal = pageData.reduce(
                      (sum: number, item: any) => sum + parseFloat(item.approvedAmount || '0'),
                      0
                    );
                    
                    return (
                      <Table.Summary fixed>
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0} colSpan={4} align="right">
                            <Text strong>{t('insurance.claims.totalAmount') || 'Total'}:</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={1} align="right">
                            <Text strong>${pageTotal.toFixed(2)}</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={2} align="right">
                            <Text strong style={{ color: '#52c41a' }}>
                              ${pageApprovedTotal.toFixed(2)}
                            </Text>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      </Table.Summary>
                    );
                  }}
                />
              ) : (
                <Empty
                  description={t('insurance.claims.items.noItems') || 'No items found in claims'}
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              )}
            </div>
          )}
        </>
      )}
      {isCollapsed && renderCollapsedView()}
    </Card>
  );
};

export default InsuranceClaimCard;

